import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '@/context/AppContext';
import api from '@/lib/api';
import { toast } from 'sonner';
import { money2, fmtDate } from '@/lib/helpers';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { AlarmClock } from 'lucide-react';

// Owner/Manager only: one-time "due today/overdue" popup on app open, plus a
// lightweight poller that toasts new sales as they happen. Presentation only.
export function NotificationsGate() {
  const { user, booting } = useApp();
  const navigate = useNavigate();
  const isManagerUp = user && (user.role === 'OWNER' || user.role === 'MANAGER' || user.owner_access);
  const [dueOpen, setDueOpen] = useState(false);
  const [dueItems, setDueItems] = useState([]);
  const lastTsRef = useRef(null);
  const initedRef = useRef(false);

  // One-time due popup
  useEffect(() => {
    if (booting || !isManagerUp) return;
    if (sessionStorage.getItem('sc_due_popup_shown')) return;
    let cancelled = false;
    (async () => {
      try {
        const d = (await api.get('/reminders?scope=open')).data.data;
        if (cancelled) return;
        const urgent = (d.items || []).filter((x) => x.due_state === 'overdue' || x.due_state === 'today');
        if (urgent.length) { setDueItems(urgent); setDueOpen(true); }
        sessionStorage.setItem('sc_due_popup_shown', '1');
      } catch (e) { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, [booting, isManagerUp]);

  // New-sale toast poller
  useEffect(() => {
    if (booting || !isManagerUp) return undefined;
    let stop = false;
    const poll = async () => {
      try {
        const d = (await api.get('/notifications?limit=15')).data.data;
        const rows = d.items || [];
        if (!initedRef.current) {
          lastTsRef.current = rows.length ? rows[0].created_at : '';
          initedRef.current = true;
        } else {
          const fresh = rows.filter((r) => (r.created_at || '') > (lastTsRef.current || ''));
          if (fresh.length) lastTsRef.current = fresh[0].created_at;
          fresh.reverse().forEach((r) => {
            toast(r.title, { description: r.body, icon: '🧾' });
          });
        }
        if (rows.length) { try { await api.post('/notifications/seen'); } catch (e) {} }
      } catch (e) { /* silent */ }
    };
    poll();
    const iv = setInterval(() => { if (!stop) poll(); }, 25000);
    return () => { stop = true; clearInterval(iv); };
  }, [booting, isManagerUp]);

  if (!isManagerUp) return null;

  return (
    <AlertDialog open={dueOpen} onOpenChange={setDueOpen}>
      <AlertDialogContent data-testid="due-popup">
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <AlarmClock className="h-5 w-5 text-amber-500" /> {dueItems.length} order{dueItems.length !== 1 ? 's' : ''} due
          </AlertDialogTitle>
          <AlertDialogDescription>These deliveries/alterations are due today or overdue.</AlertDialogDescription>
        </AlertDialogHeader>
        <div className="max-h-64 space-y-2 overflow-y-auto py-1">
          {dueItems.slice(0, 12).map((r) => (
            <div key={r.invoice_no} className="flex items-center justify-between rounded-xl border border-border p-2.5 text-sm" data-testid={`due-popup-item-${r.invoice_no}`}>
              <div className="min-w-0">
                <p className="truncate font-medium">{r.customer?.name || 'Walk-in'}</p>
                <p className="font-mono text-xs text-muted-foreground">{r.invoice_no} · {money2(r.net)}</p>
              </div>
              <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold ${r.due_state === 'overdue' ? 'bg-destructive/15 text-destructive' : 'bg-amber-500/15 text-amber-600'}`}>
                {r.due_state === 'overdue' ? 'Overdue' : 'Today'} · {fmtDate(r.due_date)}
              </span>
            </div>
          ))}
        </div>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="due-popup-dismiss">Dismiss</AlertDialogCancel>
          <AlertDialogAction onClick={() => { setDueOpen(false); navigate('/due-reminders'); }} data-testid="due-popup-view">View all</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
